import type { GridApi } from '@mui/x-data-grid-pro';
import { gridRowsMetaSelector } from '@mui/x-data-grid-pro';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { expandAncestors } from './expandAncestors';
import { useJumpToItem } from './JumpToItemContext';

const scrollRowToCenter = (apiRef: React.RefObject<GridApi>, rowIndex: number) => {
  const api = apiRef.current;
  if (!api) return;
  const { positions, currentPageTotalHeight } = gridRowsMetaSelector(apiRef);
  const rowOffset = positions[rowIndex];
  if (rowOffset === undefined) return;
  const nextOffset = positions[rowIndex + 1];
  const rowHeight =
    nextOffset !== undefined ? nextOffset - rowOffset : currentPageTotalHeight - rowOffset;
  const viewportHeight = api.getRootDimensions().viewportInnerSize.height;
  const top = Math.max(0, rowOffset + rowHeight / 2 - viewportHeight / 2);
  api.scroll({ top });
};

export function useRegisterTableJump(apiRef: React.RefObject<GridApi>) {
  const ctx = useJumpToItem();
  const apiRefStable = useRef(apiRef);
  apiRefStable.current = apiRef;

  useEffect(() => {
    if (!ctx) return;

    const probe = (itemId: number) => apiRefStable.current.current?.getRowNode(itemId) != null;

    const scroll = (itemId: number, onComplete: (success: boolean) => void) => {
      const api = apiRefStable.current.current;
      if (!api) {
        onComplete(false);
        return;
      }

      expandAncestors(apiRefStable.current, itemId);

      const finish = () => {
        const rowIndex = api.getRowIndexRelativeToVisibleRows(itemId);
        if (rowIndex === -1) {
          onComplete(false);
          return;
        }
        scrollRowToCenter(apiRefStable.current, rowIndex);
        onComplete(true);
      };

      requestAnimationFrame(() => requestAnimationFrame(finish));
    };

    return ctx.registerJumpHandler({ probe, scroll });
  }, [ctx]);

  const getHighlightClass = useCallback(
    (id: string | number) => (ctx?.highlightedItemId === id ? 'items-table-row-highlighted' : ''),
    [ctx?.highlightedItemId],
  );

  return { getHighlightClass };
}
